import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Plus } from 'lucide-react';
import type { Page, Component } from '../ComponentEditModal.types';
import { ImageCardItemCard, type ImageCardItem } from './ImageCardItemCard';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { ButtonsEditor } from '../shared/ButtonsEditor';
import type { ButtonData } from '../shared/ButtonsEditor';

interface ImageCardData {
  showTitle?: boolean;
  title?: string;
  showSubtitle?: boolean;
  subtitle?: string;
  columns?: number;
  aspectRatio?: string;
  spacing?: string;
  overlayStyle?: string;
  textPosition?: string;
  overlayOpacity?: number;
  items?: ImageCardItem[];
  customColors?: {
    background?: string;
    overlayColor?: string;
    title?: string;
    description?: string;
    buttonBg?: string;
    buttonText?: string;
  };
  buttons?: ButtonData[];
}

interface ImageCardEditorProps {
  formData: ImageCardData;
  setFormData: (data: ImageCardData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  onOpenMediaUpload: (mediaType: 'image' | 'video', assetType: string, itemIndex?: number) => void;
  currentPageComponents?: Component[];
}

export const ImageCardEditor: React.FC<ImageCardEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  onOpenMediaUpload,
  currentPageComponents,
}) => {
  const data = formData || {};
  const items = data.items || [];
  const buttons = data.buttons || [];
  const themeColors = useCustomColors();

  const addCardItem = () => {
    const newItem: ImageCardItem = {
      id: uuidv4(),
      imageUrl: '',
      cloudfrontUrl: '',
      alt: '',
      title: 'New Card',
      description: '',
      url: '',
      target: '_self',
      order: items.length,
    };
    setFormData({ ...data, items: [...items, newItem] });
  };

  const updateCardItem = (index: number, field: string, value: any) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setFormData({ ...data, items: updatedItems });
  };

  const updateCardItemFields = (index: number, fields: Record<string, any>) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], ...fields };
    setFormData({ ...data, items: updatedItems });
  };

  const removeCardItem = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index);
    updatedItems.forEach((item, i) => {
      item.order = i;
    });
    setFormData({ ...data, items: updatedItems });
  };

  const moveCardItem = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === items.length - 1)
    ) {
      return;
    }
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const updatedItems = [...items];
    [updatedItems[index], updatedItems[newIndex]] = [updatedItems[newIndex], updatedItems[index]];
    updatedItems.forEach((item, i) => {
      item.order = i;
    });
    setFormData({ ...data, items: updatedItems });
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('image-card', themeColors);

  return (
    <div className="space-y-6">
      {/* Section Headlines */}
      <HeadlinesSection data={data} setFormData={setFormData} />

      {/* Layout & Display */}
      <LayoutSection data={data} setFormData={setFormData} />

      {/* Overlay Settings */}
      <OverlaySection data={data} setFormData={setFormData} />

      {/* Cards Section */}
      <div className="border-b pb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Cards</h3>
          <button
            type="button"
            onClick={addCardItem}
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
          >
            <Plus size={16} />
            <span>Add Card</span>
          </button>
        </div>

        <div className="space-y-4">
          {items.map((item, index) => (
            <ImageCardItemCard
              key={item.id}
              item={item}
              index={index}
              totalItems={items.length}
              pages={pages}
              isLoadingPages={isLoadingPages}
              currentPageComponents={currentPageComponents}
              onUpdate={(field, value) => updateCardItem(index, field, value)}
              onUpdateFields={fields => updateCardItemFields(index, fields)}
              onRemove={() => removeCardItem(index)}
              onMove={direction => moveCardItem(index, direction)}
              onUpload={() => onOpenMediaUpload('image', 'image-card', index)}
              aspectRatio={data.aspectRatio}
            />
          ))}
        </div>
      </div>

      {/* Call-to-Action Buttons */}
      <ButtonsEditor
        buttons={buttons}
        onChange={newButtons => setFormData({ ...data, buttons: newButtons })}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
        label="Call-to-Action Buttons (Optional)"
      />

      {/* Custom Colors */}
      <ColorCustomizationPanel
        title="Colors (Optional)"
        description="Customize card colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | null | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
      />
    </div>
  );
};

// Sub-components

interface HeadlinesSectionProps {
  data: ImageCardData;
  setFormData: (data: ImageCardData) => void;
}

const HeadlinesSection: React.FC<HeadlinesSectionProps> = ({ data, setFormData }) => (
  <div className="border-b pb-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Section Headlines</h3>
    <div className="space-y-4">
      <div>
        <label className="flex items-center space-x-2 mb-2">
          <input
            type="checkbox"
            checked={data.showTitle || false}
            onChange={e => setFormData({ ...data, showTitle: e.target.checked })}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
          />
          <span className="text-sm font-medium text-gray-700">Show Section Title</span>
        </label>
        {data.showTitle && (
          <input
            type="text"
            value={data.title || ''}
            onChange={e => setFormData({ ...data, title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 mt-2"
            placeholder="Section Title"
          />
        )}
      </div>
      <div>
        <label className="flex items-center space-x-2 mb-2">
          <input
            type="checkbox"
            checked={data.showSubtitle || false}
            onChange={e => setFormData({ ...data, showSubtitle: e.target.checked })}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
          />
          <span className="text-sm font-medium text-gray-700">Show Section Subtitle</span>
        </label>
        {data.showSubtitle && (
          <input
            type="text"
            value={data.subtitle || ''}
            onChange={e => setFormData({ ...data, subtitle: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 mt-2"
            placeholder="Section Subtitle"
          />
        )}
      </div>
    </div>
  </div>
);

interface LayoutSectionProps {
  data: ImageCardData;
  setFormData: (data: ImageCardData) => void;
}

const LayoutSection: React.FC<LayoutSectionProps> = ({ data, setFormData }) => (
  <div className="border-b pb-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Layout & Display</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Columns</label>
        <select
          value={data.columns || 3}
          onChange={e => setFormData({ ...data, columns: parseInt(e.target.value) })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value={1}>1 Column</option>
          <option value={2}>2 Columns</option>
          <option value={3}>3 Columns</option>
          <option value={4}>4 Columns</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Aspect Ratio</label>
        <select
          value={data.aspectRatio || 'square'}
          onChange={e => setFormData({ ...data, aspectRatio: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="square">Square (1:1)</option>
          <option value="portrait">Portrait (3:4)</option>
          <option value="landscape">Landscape (16:9)</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Spacing</label>
        <select
          value={data.spacing || 'normal'}
          onChange={e => setFormData({ ...data, spacing: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="none">None</option>
          <option value="small">Small</option>
          <option value="normal">Normal</option>
          <option value="large">Large</option>
        </select>
      </div>
    </div>
  </div>
);

interface OverlaySectionProps {
  data: ImageCardData;
  setFormData: (data: ImageCardData) => void;
}

const OverlaySection: React.FC<OverlaySectionProps> = ({ data, setFormData }) => (
  <div className="border-b pb-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Overlay Settings</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Overlay Style</label>
        <select
          value={data.overlayStyle || 'dark'}
          onChange={e => setFormData({ ...data, overlayStyle: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="none">None</option>
          <option value="light">Light</option>
          <option value="dark">Dark</option>
          <option value="gradient">Gradient (Bottom)</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Text Position</label>
        <select
          value={data.textPosition || 'center'}
          onChange={e => setFormData({ ...data, textPosition: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="top">Left</option>
          <option value="center">Center</option>
          <option value="bottom">Right</option>
        </select>
      </div>
    </div>
    {data.overlayStyle !== 'none' && (
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Overlay Opacity: {data.overlayOpacity || 50}%
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={data.overlayOpacity || 50}
          onChange={e => setFormData({ ...data, overlayOpacity: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>
    )}
  </div>
);
